// ABOUTME: Interviews API calls for the browse/detail management surface (W2)
// ABOUTME: List summary rows + full detail with raw transcript, Q&A, and evidence by hypothesis

import { apiClient } from './client'

export interface InterviewSummary {
  interview_id: string
  interviewee: string
  group: string | null
  institution: string | null
  name: string | null
  date: string | null
  question_count: number
  evidence_count: number
  last_analyzed_at: string | null
  hypothesis_count_at_analysis: number | null
  /** content hash of the hypothesis set this interview was analyzed against */
  hypothesis_set_hash: string | null
}

export interface InterviewListResponse {
  interviews: InterviewSummary[]
  current_hypothesis_count: number
  /** hash of the live framework — rows with a different (or null) stamp are stale */
  current_framework_hash: string | null
}

export interface QAEvidence {
  evidence_id: string
  key_quote: string | null
  evidence_type: string | null
  strength: string | null
  /** Null for orphan evidence whose hypothesis link didn't resolve at ingest */
  hypothesis_id: string | null
}

export interface InterviewQA {
  question_number: number | null
  question_text: string
  answer_text: string
  evidence: QAEvidence[]
}

export interface HypothesisEvidenceGroup {
  hypothesis_id: string
  statement: string
  category: string
  items: QAEvidence[]
}

export interface InterviewDetail {
  interview_id: string
  interviewee: string
  group: string | null
  institution: string | null
  name: string | null
  date: string | null
  facilitator: string | null
  notetaker: string | null
  recording_link: string | null
  research_area: string | null
  raw_transcript: string | null
  quality_score: number | null
  last_analyzed_at: string | null
  hypothesis_count_at_analysis: number | null
  question_count: number
  evidence_count: number
  qa: InterviewQA[]
  evidence_by_hypothesis: HypothesisEvidenceGroup[]
}

export interface InterviewFilters {
  group?: string
  institution?: string
  q?: string
}

export interface DeleteResult {
  status: string
  interview_id: string
  questions_deleted: number
  evidence_deleted: number
}

export const interviewsApi = {
  async listInterviews(filters: InterviewFilters = {}): Promise<InterviewListResponse> {
    const response = await apiClient.get<InterviewListResponse>('/api/interviews', {
      params: {
        group: filters.group || undefined,
        institution: filters.institution || undefined,
        q: filters.q || undefined,
      },
    })
    return response.data
  },

  async getInterview(interviewId: string): Promise<InterviewDetail> {
    const response = await apiClient.get<InterviewDetail>(`/api/interviews/${interviewId}`)
    return response.data
  },

  async deleteInterview(interviewId: string): Promise<DeleteResult> {
    const response = await apiClient.delete<DeleteResult>(`/api/interviews/${interviewId}`)
    return response.data
  },

  async reanalyzeInterview(interviewId: string): Promise<{ job_id: string }> {
    const response = await apiClient.post<{ job_id: string }>(`/api/interviews/${interviewId}/reanalyze`)
    return response.data
  },

  async reanalyzeBatch(interviewIds?: string[]): Promise<{ job_ids: string[]; skipped: string[] }> {
    const response = await apiClient.post<{ job_ids: string[]; skipped: string[] }>(
      '/api/interviews/reanalyze-batch',
      interviewIds ? { interview_ids: interviewIds } : {},
    )
    return response.data
  },
}

export interface InterviewPatch {
  interviewee_name?: string
  interview_name?: string
  date?: string
  facilitator?: string
  notetaker?: string
  recording_link?: string
  institution?: string
  group?: string
  transcript?: string
}

export async function patchInterview(
  interviewId: string,
  patch: InterviewPatch,
): Promise<{ interview_id: string; updated: string[]; reanalysis_job_id?: string }> {
  const response = await apiClient.patch(`/api/interviews/${interviewId}`, patch)
  return response.data
}
